import store from '@/shared/store'
import { ref, computed, watch } from '@vue/composition-api'
import i18n from '@hw-core/libs/i18n'

import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

export default function useAreaList() {
  const refAreaListTable = ref(null)
  const toast = useToast()
  // Table Handlers
  const tableColumns = [
    { key: 'name', label: i18n.t('areas_management.table_headers.name'), sortable: true },
    { key: 'streetAddress', label: i18n.t('areas_management.table_headers.street_address'), sortable: true },
    { key: 'city', label: i18n.t('areas_management.table_headers.city'), sortable: true },
    { key: 'postcode', label: i18n.t('areas_management.table_headers.postcode'), sortable: true },
    { key: 'state', label: i18n.t('areas_management.table_headers.state'), sortable: true },
    { key: 'actions', label: i18n.t('areas_management.table_headers.actions') },
  ]

  const refetchData = () => {
    refAreaListTable.value.refresh()
  }

  const searchQuery = ref('')

  const capitalized = str => {
    if (str) {
      const lowercaseStr = str.toLowerCase()
      return lowercaseStr.replace(/(^\w|\s\w|\d^\w)/g, match => match.toUpperCase())
    }
    return ''
  }

  let areas = []
  let deleteId = null
  const selectedAccount = computed(() => store.state.appAccount.selectedAccount)
  const user = computed(() => store.state.appAuth.authUser)

  watch([searchQuery], () => {
    refetchData()
  })

  const onModalOpen = ({ callback }) => {
    store.dispatch('appAreas/fetchContracts', { accountId: selectedAccount.value.value.id })
      .then(response => {
        const contracts = response.map(item => ({
          label: item.name,
          value: { name: item.name, id: item.id },
        }))
        callback({ contracts })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe alueiden hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onAreaCreateEdit = ({ action, area }) => {
    const url = (action === 'create') ? 'appAreas/createArea' : 'appAreas/editArea'
    const notificationTitle = (action === 'create') ? i18n.t('areas_management.toast_message.created_success') : i18n.t('areas_management.toast_message.updated_success')
    const warningTitle = (action === 'create') ? i18n.t('areas_management.toast_message.create_error') : i18n.t('areas_management.toast_message.update_error')

    const newLocationContact = area.location.contact.filter(el => el !== null)

    const newArea = {
      ...area,
      location: {
        ...area.location,
        contact: newLocationContact,
      },
    }

    const formattedArea = {
      ...newArea,
      contract: area.contract.value.id,
    }

    store.dispatch(url,
      {
        formattedArea,
        accountId: selectedAccount.value.value.id,
      }).then(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('success'),
          text: notificationTitle,
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
      refetchData()
    })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: warningTitle,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onDeleteArea = id => {
    deleteId = id
  }

  const confirmDelete = () => {
    store
      .dispatch('appAreas/deleteArea', { id: deleteId }).then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Alue poistettu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        refetchData()
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchAreas = (ctx, callback) => {
    store
      .dispatch('appAreas/fetchAreas', {
        searchQuery: searchQuery.value,
        accountId: selectedAccount.value.value.id,
        userAreas: user.value.areas && user.value.areas.length > 0 && user.value.role === 'customer'
          ? user.value.areas
          : null,
      })
      .then(data => {
        areas = data.map(item => ({
          id: item.id,
          name: capitalized(item.name),
          emails: item.emails,
          confirmation_emails: item.confirmation_emails,
          streetAddress: capitalized(item.streetAddress),
          city: capitalized(item.city),
          postcode: capitalized(item.postcode),
          state: capitalized(item.state),
          location: item.location,
          contract: item.contract,
          category: item.category,
          requirements: item.requirements === undefined ? [] : item.requirements,
        }))
        callback(areas)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe alueiden hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // eslint-disable-next-line consistent-return
  const fetchAccountAreas = async () => {
    try {
      const accountAreas = await store.dispatch('appAreas/fetchAreas', {
        searchQuery: searchQuery.value,
        accountId: selectedAccount.value.value.id,
        userAreas: null,
      })
      const finalAreas = accountAreas.map(item => ({
        id: item.id,
        name: capitalized(item.name),
        emails: item.emails,
        confirmation_emails: item.confirmation_emails,
        streetAddress: capitalized(item.streetAddress),
        city: capitalized(item.city),
        postcode: capitalized(item.postcode),
        state: capitalized(item.state),
        location: item.location,
        contract: item.contract,
        category: item.category,
        requirements: item.requirements === undefined ? [] : item.requirements,
      }))
  
      return finalAreas
    } catch {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('error'),
          text: 'Virhe alueiden hakemisessa',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }

  return {
    refAreaListTable,
    searchQuery,
    tableColumns,
    onModalOpen,
    onAreaCreateEdit,
    onDeleteArea,
    confirmDelete,
    refetchData,
    fetchAreas,
    fetchAccountAreas,
  }
}
